import { HistoryIssueValue, Patient, PatientIssue } from '../../../../generated/api';
import PatientUtils from '../../../../services/PatientUtils';

export const HISTORY_TYPES = {
  family_history: 'Family History',
  //social_history: "Social History",
  surgical_history: 'Surgical History',
  ethnicity_history: 'Ethnicity/Race',
};

export const REFERRAL_STATUS = {
  pending_send: 'Pending Send',
  pending_reply: 'Pending Reply',
  overdue: 'Overdue',
  completed: 'Completed',
  rejected: 'Rejected',
} as const;

export const ISSUE_TYPES = {
  current: 'Current Issues',
  past: 'Past Issues',
};

export const MEDICATION_STATUS = {
  continue: {
    name: 'Continue',
  },
  discontinue: {
    name: 'Discontinue',
    reason: true,
  },
  renew: {
    name: 'Renew (Rx)',
  },
  stop: {
    name: 'Stop',
    reason: true,
  },
  hold: {
    name: 'Hold',
    reason: true,
  },
  delete: {
    name: 'Delete',
    confirmDelete: true,
  },
  edit: {
    name: 'Edit',
    edit: true,
  },
  print: {
    name: 'Print Label',
  },
};

export const LIFESTYLE_FACTORS = [
  'Smoking',
  'Nonsmoker',
  'Ex-smoker',
  'Cannabis',
  'Alcohol',
  'Exercise',
  'Sleep',
  'Illicit drugs',
  'Mental health',
  'Employment',
  'Diet',
  'Caffeine',
  'Pets',
  'Nicotine',
];

export const LIFESTYLE_MAP = [
  { name: 'Smoking', id: 's' },
  { name: 'Nonsmoker', id: 'ns' },
  { name: 'Ex-smoker', id: 'es' },
  { name: 'Cannabis', id: 'c' },
  { name: 'Alcohol', id: 'a' },
  { name: 'Exercise', id: 'e' },
  { name: 'Sleep', id: 'sl' },
  { name: 'Illicit drugs', id: 'id' },
  { name: 'Mental health', id: 'm' },
  { name: 'Employment', id: 'em' },
  { name: 'Diet', id: 'd' },
  { name: 'Caffeine', id: 'ca' },
];

export const BASIC_NOTE = ``;

export const ALLERGIES = [
  'NKDA',
  'NKA',
  'Gluten',
  'Lactose',
  'Eggs',
  'Tree nuts',
  'Peanuts',
  'Shellfish',
  'Wheat',
  'Soy',
  'Sulfa',
  'Fish',
  'Pollen',
  'Penicillin',
  'Dust Mites',
  'Mold',
  'Seasonal allergies',
  'Grass',
  'Cats',
  'Cockroaches',
];

export const MEDICATIONS = ['None'];

export const MEDICAL_ISSUES = ['Unremarkable'];

export const AUTOCOMPLETE_HISTORY = {
  preventative: [
    'Pap',
    'HPV',
    'Mammography',
    'Hemoccult',
    'Sigmoidoscopy',
    'Colonoscopy',
    'Bone Mineral Density',
    'Audioscope',
    'AAA Ultrasound',
    'LDCT',
    'Foot Exam',
    'Eye Exam',
    'Hearing Exam',
  ],
  relation: [
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grandmother',
    'Grandfather',
    'Son',
    'Daughter',
  ],
};

export const OTHER_HEALTH_RECORDS = [
  {
    id: 'allergies',
    icon: 'head-side-cough',
    name: 'Allergies',
    fields: ['name', 'notes'],
    autocomplete: { name: ALLERGIES },
  },
  {
    id: 'active_issues',
    icon: 'clipboard-list',
    name: 'Active Issues',
    fields: ['name', 'notes'],
  },
  {
    id: 'medical_issues',
    icon: 'book-medical',
    name: 'Medical History',
    fields: ['issue_type', 'name', 'notes'],
    autocomplete: { name: MEDICAL_ISSUES },
    select: {
      inputs: { issue_type: ISSUE_TYPES },
    },
  },
  {
    id: 'surgical_history',
    icon: 'user-doctor',
    name: 'Surgical History',
    fields: ['history_type', 'name', 'details', 'notes'],
    exclude: (item: HistoryIssueValue) =>
      !!item.history_type && item.history_type !== HISTORY_TYPES.surgical_history,
    values: { history_type: HISTORY_TYPES.surgical_history },
    detailsLabel: 'name',
  },
  {
    id: 'medications',
    icon: 'prescription-bottle',
    name: 'Medications',
    fields: ['name', 'notes'],
    autocomplete: { name: MEDICATIONS },
  },
  {
    id: 'ethnicity_history',
    icon: 'users-rays',
    name: 'Ethnicity/Race',
    fields: ['history_type', 'name', 'details', 'notes'],
    exclude: (item: HistoryIssueValue) =>
      !!item.history_type && item.history_type !== HISTORY_TYPES.ethnicity_history,
    values: { history_type: HISTORY_TYPES.ethnicity_history },
    detailsLabel: 'name',
  },
  {
    id: 'history',
    icon: 'clock-rotate-left',
    name: 'History',
    hideOnEmpty: true,
    fields: ['history_type', 'name', 'details', 'notes'],
    detailsLabelFn: (historyType: string) => {
      switch (historyType) {
        case HISTORY_TYPES.family_history:
          return 'relation';
        default:
          return 'name';
      }
    },
    autocomplete: AUTOCOMPLETE_HISTORY,
    select: {
      inputs: { history_type: HISTORY_TYPES },
    },
  },
  {
    id: 'family_history',
    icon: 'family',
    name: 'Family History',
    fields: ['history_type', 'name', 'details', 'notes'],
    exclude: (item: HistoryIssueValue) =>
      !!item.history_type && item.history_type !== HISTORY_TYPES.family_history,
    values: { history_type: HISTORY_TYPES.family_history },
    detailsLabel: 'relation',
    autocomplete: {
      relation: AUTOCOMPLETE_HISTORY.relation,
    },
  },
  {
    id: 'gtpal',
    icon: 'person-pregnant',
    name: 'GTPAL',
    fields: ['name', 'notes'],
    hide: (patient: Patient) => PatientUtils.getGender(patient) === 'Male',
  },
  {
    id: 'preventative_screenings',
    icon: 'microscope',
    name: 'Preventive Screenings',
    fields: ['name', 'notes'],
    autocomplete: { name: AUTOCOMPLETE_HISTORY.preventative },
  },
  {
    id: 'lifestyle_factors',
    icon: 'dumbbell',
    name: 'Lifestyle Factors',
    fields: ['name', 'details', 'notes'],
    autocomplete: { name: LIFESTYLE_FACTORS },
  },
  {
    id: 'consultants',
    icon: 'users-medical',
    name: 'Consultants',
    fields: ['name', 'specialty', 'notes'],
  },
];

export const METRICS = [
  { label: 'T', metric: 'temperature', name: 'Temperature' },
  {
    label: 'Sitting BP',
    metric: 'bp/Sitting',
    name: 'Sitting Blood Pressure',
    width: '80px',
  },
  {
    label: 'Standing BP',
    metric: 'bp/Standing',
    name: 'Standing Blood Pressure',
    width: '100px',
  },
  {
    label: 'Supine BP',
    metric: 'bp/Supine',
    name: 'Supine Blood Pressure',
    width: '90px',
  },
  {
    label: 'Average BP',
    metric: 'bp/Average',
    name: 'Average Blood Pressure',
    width: '90px',
  },
  { label: 'Sitting HR', metric: 'hr/Sitting', name: 'Sitting Heart Rate', width: '90px' },
  { label: 'Standing HR', metric: 'hr/Standing', name: 'Standing Heart Rate', width: '100px' },
  { label: 'RR', metric: 'rr', name: 'Respiratory Rate' },
  { label: 'O2', metric: 'o2', name: 'Oxygen' },
  { label: 'BS', metric: 'glucose', name: 'Blood Sugar' },
  { label: 'HT', metric: 'height', name: 'Height' },
  { label: 'WT', metric: 'weight', name: 'Weight' },
  { label: 'WC', metric: 'waist_circumference', name: 'Waist Circumference' },
  { label: 'A1C', metric: 'a1c', name: 'Hemoglobin A1C' },
  { label: 'FRS', metric: 'frs', name: 'Framingham Risk Score' },
  { label: 'BMI', metric: 'bmi', name: 'Body Mass Index', disabled: true },
];
